import { PropType } from 'vue';
import { mapGetters } from 'vuex';
import plantsClient from '@/core/services/plants';
import { Vue, Options } from 'vue-class-component';
import {
  RoomDTO,
  RoomType,
  DeviceDTO,
  ServerModel,
  PlantDetailDTO,
} from '@/../client-generator/generated-clients/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import { NamingService } from '@/core/services/naming.service';
import Room from './PlantDashboardRoom/PlantDashboardRoom.vue';
import {
  getMxServerSettingsLabel,
  getPlantIconImageUrlsByType,
} from '@/core/utils/utils';
import CardContainer from '@/views/components/CardContainer/CardContainer.vue';
import PlantDashboardDevices from './PlantDashboardDevices/PlantDashboardDevices.vue';
import { PlantEntityType } from '@/../client-generator/generated-clients/configurations';

@Options({
  name: 'PlantDashboard',
  components: {
    Room,
    PlantDashboardDevices,
    CardContainer,
  },
  props: {
    currentPlant: {
      type: Object as PropType<PlantDetailDTO>,
      required: false,
    },
  },
  emits: ['add-object', 'add-structure', 'update-list'],
  computed: {
    ...mapGetters('UI', ['plantAddRoomEnabled']),
  },
  watch: {
    currentPlant() {
      this.sortAndGroupRooms();
    },
  },
})
export default class PlantDashboard extends Vue {
  plantAddRoomEnabled!: boolean;
  radioValue = true;
  currentPlant?: PlantDetailDTO;
  plantDevices: DeviceDTO[] = [];

  get mxServerLabel() {
    return getMxServerSettingsLabel(this.currentPlant);
  }

  get imageURLs() {
    return getPlantIconImageUrlsByType(
      this.currentPlant?.icon,
      this.currentPlant?.customIconImageUrl,
      this.currentPlant?.plantType,
    );
  }

  get imageSrcSetURLs() {
    if (this.imageURLs.length <= 1) return this.imageURLs[0];
    return `${this.imageURLs[0]}, ${this.imageURLs[1]} 2x, ${this.imageURLs[2]} 3x`;
  }

  get lastScanDate() {
    if (this.currentPlant?.lastScanDate) {
      try {
        const theDate = new Date(this.currentPlant.lastScanDate as Date);
        return theDate.getDay() === new Date().getDay()
          ? this.$t('AppResource.Today')
          : theDate.toLocaleDateString();
      } catch (error) {
        return '';
      }
    }
    return '';
  }

  get lastScanTime() {
    if (this.currentPlant?.lastScanDate) {
      try {
        const theDate = new Date(this.currentPlant.lastScanDate as Date);
        return theDate.toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: 'numeric',
        });
      } catch (error) {
        return '';
      }
    }
    return '';
  }

  get serverIcon() {
    switch (this.currentPlant?.mxServer?.model) {
      case ServerModel.Classe300EOS:
        return require('@/assets/images/icons/icon-mx-server.svg');
      case ServerModel.F460:
      case ServerModel.F461:
        return require('@/assets/images/icons/icon-server-f460.svg');
      case ServerModel.MyHomeServer1:
        return require('@/assets/images/icons/icon-server-mhs-1.svg');
      default:
        return require('@/assets/images/icons/icon-server-mhs-1.svg');
    }
  }

  get showAlarmSystem() {
    return this.currentPlant?.mxServer?.isAlarmConfigured;
  }

  async mounted() {
    this.fetchDevices();
    await NamingService.prefetchFlatEntities(
      +this.currentPlant?.desktopId!,
      PlantEntityType.ROOM
    );

    this.sortAndGroupRooms();
  }

  private sortAndGroupRooms() {
    const orderedRooms: { type: RoomType; rooms: RoomDTO[] }[] = [];
    this.currentPlant?.rooms?.forEach((r) => {
      const roomTypeIdx = orderedRooms.findIndex((x) => x.type === r.type);
      roomTypeIdx !== -1
        ? orderedRooms[roomTypeIdx].rooms.push(r)
        : orderedRooms.push({
            type: r.type!,
            rooms: [r],
          });
    });

    orderedRooms.forEach((rt) => {
      rt.rooms = rt.rooms.sort((a, b) => a.name?.localeCompare(b.name!)!);
    });

    this.currentPlant!.rooms = orderedRooms.flatMap((r) => r.rooms);
  }

  toggleAddRoomObjectDialog(room: RoomDTO) {
    this.$emit('add-object', room);
  }

  toggleCreateStructureDialog() {
    this.$emit('add-structure');
  }

  async fetchDevices() {
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      this.plantDevices = await plantsClient.allDevices(
        this.currentPlant?.desktopId as number
      );
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
      console.log(error);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  goToEosSettings() {
    this.$router.push({
      name: 'page-plant-settings',
    });
  }

  goToGeneralCommands() {
    this.$router.push({
      name: 'page-commands-groups',
    });
  }

  goToScenes() {
    this.$router.push({
      name: 'page-plant-scenes',
    });
  }

  goToAlarmSystem() {
    this.$router.push({
      name: 'page-plant-alarmsystem',
    });
  }
}
